// Camera rigs: where each kind of shot puts the camera, as a pure function of the race.
// World: x runs along the track (start 0, finish TRACK_LENGTH), lanes along z, the grandstand
// on -z and the lake on +z. Most broadcast shots sit on the lake side looking at the stand,
// so the crowd fills the background. Cats are about 0.5 m long, so close shots are a few metres away.
import { LANE_COUNT, LANE_WIDTH, TRACK_LENGTH, laneCenterZ } from '../data/track';
import type { CatId, CatSnapshot, RaceSnapshot } from '../race/types';
import { GANTRY, HOARDING, PODIUM } from '../world/environment/layout';
import { type RigPose, type ShotKind, type Vec3, v } from './types';

const HALF = (LANE_COUNT * LANE_WIDTH) / 2;
const clamp = (n: number, a: number, b: number) => Math.min(b, Math.max(a, n));
/** Keeps a lake-side camera in front of the advertising boards, so shots of the outer lane do not look through their back. */
const inBoards = (z: number) => Math.min(z, HOARDING.z - 0.6);
const smooth = (a: number, b: number, t: number) => { const u = clamp((t - a) / (b - a), 0, 1); return u * u * (3 - 2 * u); };

/**
 * How high a cat floats on catnip. Matches the cats job's stand-in float (FLOAT_HEIGHT in
 * world/cats/catActor.ts); the catnip trip job should keep these in step if it lifts cats higher.
 */
export const TRIP_FLOAT_HEIGHT = 1.1;

export interface TripInfo {
  cat: CatId;
  lane: number;
  /** Race position where the cat caught the catnip, 0..1. */
  from: number;
  /** Where it lands, once the signal (seen 2 s ahead) says so. */
  to: number | null;
  /** Seconds since the catch. */
  elapsed: number;
}

export interface RigContext {
  snapshot: RaceSnapshot;
  subject: CatSnapshot | null;
  /** Seconds since this shot started. */
  shotTime: number;
  trip: TripInfo | null;
  /** The cat fighting the subject for a place (duel and photo finish shots). */
  rival?: CatSnapshot | null;
  /** 0..1: how far the finish camera has pushed in on a tight finish, or the finish replay has run. */
  push?: number;
}

export const catX = (c: CatSnapshot) => c.displayPos * TRACK_LENGTH;

export function leader(s: RaceSnapshot): CatSnapshot | null {
  return s.cats.find((c) => c.place === 1) ?? s.cats[0] ?? null;
}

/** The first cat still racing: once the winner is home, the race is about who is next. */
export function runningLeader(s: RaceSnapshot): CatSnapshot | null {
  const live = s.cats.filter((c) => !c.finished).sort((a, b) => a.place - b.place);
  return live[0] ?? leader(s);
}

/** The leading group: cats within 25 m of the leader that are still running. */
export function pack(s: RaceSnapshot): { mid: number; spread: number; lead: number } {
  const xs = s.cats.filter((c) => !c.tripping).map(catX);
  if (!xs.length) return { mid: 0, spread: 0, lead: 0 };
  const lead = Math.max(...xs);
  const group = xs.filter((x) => lead - x < 25);
  const back = Math.min(...group);
  return { mid: (lead + back) / 2, spread: lead - back, lead };
}

/** Where the cats still racing are (all cats once everyone is home). */
function field(s: RaceSnapshot): { from: number; to: number } {
  const live = s.cats.filter((c) => !c.finished);
  const xs = (live.length ? live : s.cats).map(catX);
  return xs.length ? { from: Math.min(...xs), to: Math.max(...xs) } : { from: 0, to: 0 };
}

export function rig(kind: ShotKind, ctx: RigContext): RigPose {
  const pose = place(kind, ctx);
  // Shots on a cat travel with it; shots on the pack travel with the pack centre.
  if (ctx.subject && !pose.anchor && kind !== 'finishLine' && kind !== 'podium' && kind !== 'gates' && kind !== 'overview' && kind !== 'photoFinish') {
    pose.anchor = v(catX(ctx.subject), 0, laneCenterZ(ctx.subject.lane));
  }
  return pose;
}

function place(kind: ShotKind, ctx: RigContext): RigPose {
  const { snapshot: s, subject: c, shotTime: t } = ctx;
  const cx = c ? catX(c) : pack(s).lead;
  const cz = c ? laneCenterZ(c.lane) : 0;
  const speed = c?.speed ?? 0;
  switch (kind) {
    case 'gates': {
      // A slow dolly along the row of stalls while the cats wait.
      const dolly = clamp(t * 0.35, 0, 3);
      return { position: v(9 - dolly, 1.2, HALF + 2.5), target: v(-0.6, 0.55, -1.5), fov: 44, follow: 3, handheld: 0.6 };
    }
    case 'chase':
      return {
        position: v(cx - 4.2, 1.15, inBoards(cz + 2.1)), target: v(cx + 2.8, 0.45, cz),
        fov: clamp(46 + speed * 1.3, 46, 64), follow: 7, handheld: 1,
      };
    case 'frontQuarter':
      return { position: v(cx + 6.2 + speed * 0.15, 1.2, inBoards(cz + 4)), target: v(cx - 0.4, 0.45, cz), fov: 38, follow: 5, handheld: 0.8 };
    case 'pack': {
      const p = pack(s);
      const back = 8 + p.spread * 0.55;
      return { position: v(p.mid + 3 + p.spread * 0.15, 1.8 + p.spread * 0.1, HALF + back), target: v(p.mid, 0.3, -1), fov: 42, follow: 3.5, handheld: 0.7, anchor: v(p.mid, 0, 0) };
    }
    case 'overview': {
      const f = field(s);
      const span = clamp(f.to - f.from + 40, 60, 220);
      const centre = (f.from + f.to) / 2;
      return { position: v(centre - span * 0.32, 8 + span * 0.26, HALF + span * 0.5), target: v(centre + span * 0.06, 0, -1), fov: 42, follow: 1.8, handheld: 0, anchor: v(centre, 0, 0) };
    }
    case 'finishLine':
      // Low and locked off just past the line, inside the boards and clear of the gantry tower,
      // looking back up the lanes so the cats run at the camera across the chequered line.
      // On a tight finish it pushes in slowly as the cats come (push 0..1, see director.ts).
      return { position: v(TRACK_LENGTH + 3.5, 0.6, HALF + 0.8), target: v(TRACK_LENGTH - 30, 0.5, -1.5), fov: 38 - 8 * (ctx.push ?? 0), follow: 10, handheld: 0 };
    case 'duel': {
      // Side on and low, level with the pair, so the gap between them reads at a glance.
      const r = ctx.rival;
      const rx = r ? catX(r) : cx;
      const rz = r ? laneCenterZ(r.lane) : cz;
      const mid = (cx + rx) / 2;
      const gap = Math.abs(cx - rx);
      const dz = Math.abs(cz - rz);
      return {
        position: v(mid + 1.8 + gap * 0.3, 1 + dz * 0.12, inBoards(Math.max(cz, rz) + 3.4 + gap * 0.25)),
        target: v(mid - 0.2, 0.4, (cz + rz) / 2), fov: clamp(34 + gap * 2 + dz * 2.2, 34, 58), follow: 4, handheld: 0.35,
        anchor: v(mid, 0, 0), anchorEase: 0,
      };
    }
    case 'photoFinish': {
      // The finish video: square on to the line from high on the lake side, inside the gantry
      // tower so it never blocks the view, panning with the pair until they reach the line, then
      // locked off and slowly pushing in while the replay runs.
      const r = ctx.rival;
      const rx = r ? catX(r) : cx;
      const rz = r ? laneCenterZ(r.lane) : cz;
      const x = Math.min((cx + rx) / 2, TRACK_LENGTH + 3);
      const dz = Math.abs(cz - rz);
      return {
        position: v(x + 0.6, 3.2, Math.min(HALF + 0.9, GANTRY.towerZ - 0.8)), target: v(x, 0.35, (cz + rz) / 2),
        fov: clamp(30 + dz * 1.6, 30, 46) - 4 * (ctx.push ?? 0), follow: 6, handheld: 0, anchor: v(TRACK_LENGTH, 0, 0),
      };
    }
    case 'catch': {
      // Close on the cat as it bites the leaf, tilting up as it starts to float.
      const trip = ctx.trip;
      const x = trip ? trip.from * TRACK_LENGTH : cx;
      const lift = smooth(0, 1.2, trip?.elapsed ?? t) * TRIP_FLOAT_HEIGHT;
      return { position: v(x + 3.3, 0.9 + lift * 0.4, inBoards(cz + 2.4)), target: v(x, 0.4 + lift, cz), fov: 40, follow: 4, handheld: 0.5, anchor: v(x, 0, cz) };
    }
    case 'tripFollow': {
      const trip = ctx.trip;
      const x = trip ? trip.from * TRACK_LENGTH : cx;
      const z = trip ? laneCenterZ(trip.lane) : cz;
      const e = trip?.elapsed ?? t;
      const rise = smooth(0, 1.5, e) * TRIP_FLOAT_HEIGHT; // the cat floats up out of its lane
      const a = 0.9 + e * 0.22; // a slow orbit around the floating cat
      return {
        position: v(x + Math.cos(a) * 4.2, 0.8 + rise * 1.1, inBoards(z + Math.sin(a) * 4.2)),
        target: v(x, 0.25 + rise, z), fov: 44, follow: 3, handheld: 0.4, anchor: v(x, 0, z),
      };
    }
    case 'landingHigh': {
      const trip = ctx.trip;
      const lane = trip?.lane ?? c?.lane ?? 0;
      const x = (trip?.to ?? trip?.from ?? (c ? c.displayPos : 0)) * TRACK_LENGTH;
      const z = laneCenterZ(lane);
      return { position: v(x - 7, 11, z + 8), target: v(x + 1, 0, z), fov: 46, follow: 2.5, handheld: 0.3, anchor: v(x, 0, z), anchorEase: 1.5 };
    }
    case 'ham':
      return { position: v(cx + 2.1, 0.95, inBoards(cz + 3.1)), target: v(cx, 0.4, cz), fov: 36, follow: 5, handheld: 0.5 };
    case 'straggler':
      // The lonely last cat, small in a wide frame.
      return { position: v(cx - 7, 2.6, cz + 6), target: v(cx + 5, 0.3, cz), fov: 52, follow: 4, handheld: 0.9 };
    case 'podium': {
      const a = 0.55 * Math.sin(t * 0.14);
      const r = 9 - clamp(t * 0.15, 0, 2.5);
      return {
        position: v(PODIUM.x + Math.sin(a) * r, 2.3, PODIUM.z - Math.cos(a) * r),
        target: v(PODIUM.x, 1.5, PODIUM.z + 0.5), fov: 40, follow: 3, handheld: 0.3,
      };
    }
  }
}

export function lerpVec(a: Vec3, b: Vec3, k: number): Vec3 {
  return v(a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k, a.z + (b.z - a.z) * k);
}
